import { apiCall, ApiResult } from './core';

export const peopleAPI = {
  // Get partner employees (for reuse across projects)
  getPartnerEmployees: async (): Promise<ApiResult> => {
    try {
      const data = await apiCall('/employees');
      return { success: true, data: data.employees || [] };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Get all people for a project
  getProjectPeople: async (projectId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/people`);
      return { success: true, data: data.people || [] };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Add a person to a project
  addProjectPerson: async (projectId: string, personData: Record<string, any>): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/people`, {
        method: 'POST',
        body: JSON.stringify(personData)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Update a person
  updateProjectPerson: async (projectId: string, personId: string, updates: Record<string, any>): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/people/${personId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Remove a person from a project
  removeProjectPerson: async (projectId: string, personId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/people/${personId}`, {
        method: 'DELETE'
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  }
};

export const timesheetAPI = {
  // Get timesheet entries for a project
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic timesheet data
  getProjectTimesheets: async (projectId: string, filters: Record<string, string> = {}): Promise<{ success: boolean; data?: any[]; summary?: any; error?: string }> => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = `/projects/${projectId}/timesheets${queryParams ? '?' + queryParams : ''}`;
      const data = await apiCall(endpoint);
      return { success: true, data: data.entries || [], summary: data.summary || {} };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Add a timesheet entry
  addTimesheetEntry: async (projectId: string, entryData: Record<string, any>): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/timesheets`, {
        method: 'POST',
        body: JSON.stringify(entryData)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Update a timesheet entry
  updateTimesheetEntry: async (projectId: string, entryId: string, updates: Record<string, any>): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/timesheets/${entryId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Delete a timesheet entry
  deleteTimesheetEntry: async (projectId: string, entryId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/timesheets/${entryId}`, {
        method: 'DELETE'
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Get people assigned to a project
  getProjectPeople: async (projectId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/people`);
      return { success: true, data: data.people || [] };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Add person to project (existing or new)
  addProjectPerson: async (projectId: string, personData: Record<string, any>): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/people`, {
        method: 'POST',
        body: JSON.stringify(personData)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Update person in project
  updateProjectPerson: async (projectId: string, personId: string, updates: Record<string, any>): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/people/${personId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Remove person from project
  removeProjectPerson: async (projectId: string, personId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/people/${personId}`, {
        method: 'DELETE'
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Get partner employees (for selection)
  getPartnerEmployees: async (): Promise<ApiResult> => {
    try {
      const data = await apiCall('/employees');
      return { success: true, data: data.employees || [] };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Get hours worked by person across all projects
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic timesheet data
  getHoursByPerson: async (filters: Record<string, string> = {}): Promise<{ success: boolean; data?: any[]; totalHours?: number; error?: string }> => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = `/timesheets/hours-by-person${queryParams ? '?' + queryParams : ''}`;
      const data = await apiCall(endpoint);
      return { success: true, data: data.items || [], totalHours: data.total_hours || 0 };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  }
};
